import { SearchHistory } from "./components/SearchHistory";
import { Spinner } from "@/view/components/ui/spinner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/view/components/ui/sheet";
import TransactionDetails from "../Dashboard/components/TransactionTable/components/TransactionDetails";
import { TransactionSupport } from "../Dashboard/components/TransactionTable/components/TransactionDetails/components/TransactionSupport";
import { UploadFiles } from "../Dashboard/components/TransactionTable/components/TransactionDetails/components/UploadFiles";
import { useSupportController } from "./useSupportController";
import { SupportSearchResults } from "./components/SupportSearchResults";
import { PendingDocuments } from "./components/PendingDocuments";
import { SupportRequestQueue } from "./components/SupportRequestQueue";
import { SupportListTabs } from "./components/SupportListTabs";
import { ArrowLeft, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { ObservationsBackButton } from "../Dashboard/components/TransactionTable/components/TransactionDetails/components/ObservationsBackButton";
import { SupportRequestDetails } from "./components/SupportRequestDetails";
import { useSetAtom } from "jotai";
import { selectedIdfromTableRowTransaction } from "@/app/store";
import { Input } from "@/view/components/ui/input";
import { cn } from "@/app/utils/cn";

export const Support = () => {
  const {
    data,
    handleOpenCustomer,
    handleOpenSeller,
    handleOpenTeam,
    handleOpenTransactionInNewTab,
    handleOpenSupportRequestInNewTab,
    handleDocumentRowClick,
    handleSearch,
    isLoading,
    selectedId,
    results,
    searchTotals,
    searchPages,
    pending,
    errors,
    documentsTransactions,
    isLoadingDocuments,
    searchQuery,
    searchTerm,
    setSheetOpen,
    sheetOpen,
    productsCatalog,
    selectedSupport,
    supportDetail,
    comment,
    showSupportTools,
    showSupportRequests,
    supportTab,
    setSupportTab,
    setComment,
    handleSelectSupport,
    isUpdatingSupport,
    handleSaveComment,
    handleUpdateStatus,
    supportTeam,
    assignSupport,
    isAssigningSupport,
  } = useSupportController();

  const [step, setStep] = useState(0);

  const selectedRequest = selectedSupport;

  const setSelectedTransactionId = useSetAtom(
    selectedIdfromTableRowTransaction,
  );

  const handleOpenTransaction = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setSheetOpen(true);
  };

  const title: Record<number, string> = {
    0: "Detalhes da transação",
    1: "Solicitar informações",
    2: "Documentos",
  };

  const isSearching = searchQuery !== "" && searchTerm !== "";

  const isShowingPendingDocuments =
    !selectedRequest && showSupportTools && !isSearching;

  useEffect(() => {
    if (sheetOpen) {
      setStep(0);
    }
  }, [sheetOpen]);

  return (
    <div className="flex flex-col lg:h-full">
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-[340px] overflow-y-auto rounded-2xl bg-[#F4F5F7] p-2 xs:mr-5 xs:mt-5 xs:h-[95%] xs:w-96 xs:p-5">
          <div className="min-h-[800px]">
            <SheetHeader>
              <SheetTitle className="text-xl font-semibold">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {step > 0 && (
                      <ArrowLeft
                        size={20}
                        onClick={() => setStep(0)}
                        className="cursor-pointer"
                      />
                    )}
                    {step === 0 && <ObservationsBackButton />}
                    <h2>{title[step]}</h2>
                  </div>
                </div>
                <div className="my-5 w-full border-b-[1px] border-gray01" />
              </SheetTitle>
            </SheetHeader>
            {selectedId && step === 0 && (
              <TransactionDetails
                onSupport={() => setStep(1)}
                onDocuments={() => setStep(2)}
              />
            )}
            {selectedId && step === 1 && <TransactionSupport />}
            {selectedId && step === 2 && (
              <UploadFiles onBack={() => setStep(0)} />
            )}
          </div>
        </SheetContent>
      </Sheet>
      <div className="flex flex-col gap-6 pb-10 lg:min-h-0 lg:flex-1">
        <div>
          <h1 className="text-2xl font-semibold">Suporte</h1>
          {!showSupportTools && (
            <p className="mt-5 font-medium text-black04">
              Fila de chamados do time de suporte
            </p>
          )}
        </div>

        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {showSupportRequests && (
            <SupportListTabs value={supportTab} onChange={setSupportTab} />
          )}

          {showSupportTools && (
            <div className="relative w-full lg:max-w-[440px]">
              <Search className="absolute left-3 top-1/2 h-6 w-6 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Buscar por CPF, contrato, chamado, nome do cliente ou vendedor"
                className="rounded-full pl-10"
              />
            </div>
          )}
        </div>

        <div
          className={cn(
            "grid grid-cols-1 gap-6 lg:min-h-0 lg:flex-1",
            showSupportRequests
              ? selectedRequest
                ? "lg:grid-cols-[300px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(260px,22%)_minmax(0,1fr)]"
                : "lg:grid-cols-[300px_minmax(0,1fr)] lg:grid-rows-[minmax(0,1fr)_minmax(0,1fr)] xl:grid-cols-[minmax(260px,22%)_minmax(0,1fr)_minmax(260px,24%)] xl:grid-rows-[minmax(0,1fr)]"
              : "lg:grid-cols-[minmax(0,1fr)_minmax(260px,30%)] lg:grid-rows-[minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_minmax(260px,24%)]",
          )}
        >
          {showSupportRequests && (
            <SupportRequestQueue
              className="max-h-[720px] lg:row-span-2 lg:h-full lg:max-h-none xl:row-span-1"
              tab={supportTab}
              selectedId={selectedRequest?._id}
              onSelect={handleSelectSupport}
            />
          )}

          <div
            className={cn(
              "flex min-h-0 min-w-0 flex-col rounded-xl lg:h-auto",
              !isSearching && "max-h-[720px] overflow-y-auto lg:max-h-none",
              !isSearching &&
                !isShowingPendingDocuments &&
                "border bg-card p-4",
            )}
          >
            {selectedRequest ? (
              <div className="flex flex-col gap-4">
                <SupportRequestDetails
                  data={supportDetail ?? selectedRequest}
                  comment={comment}
                  setComment={setComment}
                  isPending={isUpdatingSupport}
                  handleSaveComment={handleSaveComment}
                  handleUpdateStatus={handleUpdateStatus}
                  onOpenTransaction={handleOpenTransaction}
                  onOpenSeller={handleOpenSeller}
                  supportTeam={supportTeam}
                  onAssign={assignSupport}
                  isAssigning={isAssigningSupport}
                  productsCatalog={productsCatalog}
                />
              </div>
            ) : !showSupportTools ? (
              <p className="rounded-xl border border-dashed border-gray01 bg-gray-50 p-3 text-center text-sm text-black04">
                Selecione um chamado na fila para ver os detalhes
              </p>
            ) : (
              <>
                {isSearching ? (
                  <SupportSearchResults
                    key={searchTerm}
                    query={searchTerm}
                    results={results}
                    pending={pending}
                    errors={errors}
                    onOpenCustomer={handleOpenCustomer}
                    onOpenSeller={handleOpenSeller}
                    onOpenTransaction={handleOpenTransactionInNewTab}
                    onOpenTeam={handleOpenTeam}
                    onOpenSupportRequest={handleOpenSupportRequestInNewTab}
                    productsCatalog={productsCatalog}
                    searchPages={searchPages}
                    searchTotals={searchTotals}
                  />
                ) : (
                  <PendingDocuments
                    data={documentsTransactions}
                    isLoading={isLoadingDocuments}
                    onSelect={handleDocumentRowClick}
                    selectedId={selectedId}
                    showQueueHint={showSupportRequests}
                    productsCatalog={productsCatalog}
                  />
                )}
              </>
            )}
          </div>

          {!selectedRequest && showSupportTools && (
            <div className="flex max-h-[720px] min-h-0 min-w-0 flex-col overflow-y-auto rounded-xl border bg-card p-4 lg:max-h-none">
              {isLoading ? (
                <div className="flex h-96 items-center justify-center">
                  <Spinner />
                </div>
              ) : (
                data && (
                  <SearchHistory
                    handleMakeFetchAgain={handleSearch}
                    supports={data.supports}
                  />
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
